import { SQUAD_COLORS } from '../templateConstants';
import {
  SQUAD_PAGE_HEADER,
  SQUAD_BODY_LAYOUT,
  SQUAD_TYPOGRAPHY,
  SQUAD_SECTION_GEOMETRY_PREVIEW,
} from '../pageHeaderTokens';
import sharedStyles from './sharedStyles';

const { sidebarWidth, sidebarMaxWidth, columnGap } = SQUAD_BODY_LAYOUT;

const sidebarColumn = {
  width: sidebarWidth,
  maxWidth: sidebarMaxWidth,
  flexShrink: 0,
};

const styles = {
  ...sharedStyles,
  page: {
    width: 794,
    maxWidth: '100%',
    minHeight: 1123,
    boxSizing: 'border-box' as const,
    p: '22px',
    border: '1px solid #e5e7eb',
    borderRadius: 2,
    backgroundColor: SQUAD_COLORS.white,
    fontFamily: 'SegoeUI, "Segoe UI", system-ui, -apple-system, sans-serif',
    color: SQUAD_COLORS.text,
    position: 'relative' as const,
    overflow: 'hidden',
    mb: 3,
    display: 'flex',
    flexDirection: 'column' as const,
    '& *': {
      fontFamily: 'inherit',
    },
  },
  bodyRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'stretch',
    flex: 1,
    gap: `${columnGap}px`,
    pt: `${SQUAD_PAGE_HEADER.bodyRowPaddingTop}px`,
    minHeight: 0,
  },
  sidebar: {
    ...sidebarColumn,
    backgroundColor: SQUAD_COLORS.sidebarBg,
    // Match PDF sidebar inset (pdfSidebarPaddingHorizontal/Bottom = 7 pt on the 180 rail).
    px: `${SQUAD_PAGE_HEADER.pdfSidebarPaddingHorizontal}px`,
    // Starts level with the first navy bar, which carries navyBarMarginTop — mirrors
    // PDF_RAIL_TOP in PdfPreviewDialog/styles.ts.
    mt: `${SQUAD_SECTION_GEOMETRY_PREVIEW.navyBarMarginTop}px`,
    pt: `${SQUAD_PAGE_HEADER.pdfSidebarPaddingTop}px`,
    pb: `${SQUAD_PAGE_HEADER.pdfSidebarPaddingBottom}px`,
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: '100%',
  },
  mainCol: {
    flex: 1,
    minWidth: 0,
    minHeight: '100%',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
    '& > *': {
      marginBottom: 0,
    },
  },
  section: {
    mb: '18px',
  },
  mainSection: {
    mb: 0,
  },
  bodyText: {
    color: '#000',
    fontSize: SQUAD_TYPOGRAPHY.body,
    lineHeight: 1.31,
    textAlign: 'justify' as const,
    mb: 0,
  },
  /** Anlagestrategie / Monatskommentar — tighter leading than general body. */
  proseText: {
    color: '#000',
    fontSize: SQUAD_TYPOGRAPHY.body,
    lineHeight: SQUAD_SECTION_GEOMETRY_PREVIEW.proseLineHeight,
    textAlign: 'justify' as const,
    mb: 0,
  },
  /** Mirrors the PDF: no coloured ground, that was only a reminder marker. */
  footnote: {
    fontSize: SQUAD_TYPOGRAPHY.footnote,
    lineHeight: 1.3,
    color: SQUAD_COLORS.navy,
    px: 0,
    py: 0,
  },
  /** Under chart / performance table — PDF uses SQUAD_TYPOGRAPHY_PDF.small + muted. */
  chartFootnote: {
    fontSize: SQUAD_TYPOGRAPHY.caption,
    lineHeight: 1.3,
    color: SQUAD_COLORS.muted,
    mt: 0.5,
    whiteSpace: 'pre-line' as const,
  },
  /**
   * Charts the PDF takes as a snapshot of the preview (NAV chart, rolling bars, raster
   * pies): no mouse input. A drag over the NAV chart zoomed it in, and that zoomed view
   * went into the published PDF (found 06.10.2026).
   */
  capturedChart: {
    pointerEvents: 'none' as const,
    userSelect: 'none' as const,
  },
  page4Full: {
    pt: 1,
  },
  /** Frame and inactive numbers in `border`; only the active box takes the fund colour. */
  riskBox: (active: boolean, border: string, linked = false, activeFill: string = border) => ({
    flex: 1,
    height: 24,
    border: `1px solid ${border}`,
    marginLeft: linked ? '-1px' : 0,
    backgroundColor: active ? activeFill : SQUAD_COLORS.white,
    color: active ? SQUAD_COLORS.white : border,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: SQUAD_TYPOGRAPHY.riskBox,
    fontWeight: 700,
  }),
};

export default styles;
